/**
 * Reports Page
 * Report templates, saved reports, and export history (unified)
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FileText, Download, FileBarChart, Users, Settings, BarChart3,
  CheckCircle, Clock, XCircle, Trash2, Search, ChevronUp, ChevronDown,
} from 'lucide-react';
import Spinner from '../components/shared/Spinner';
import EmptyState from '../components/shared/EmptyState';
import RefreshButton from '../components/shared/RefreshButton';
import DateRangeFilter from '../components/dashboard/DateRangeFilter';
import useDateRange from '../hooks/useDateRange';
import Pagination from '@shared/components/Pagination';
import { PermissionGate, usePermissions } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';
import usePagination from '@shared/hooks/usePagination';
import {
  getReports, createReport, exportReport, deleteReport,
  getExports, downloadExport,
} from '../services/analyticsService';

// ── Constants ──

const TABS = [
  { key: 'templates', label: 'Templates' },
  { key: 'reports', label: 'Saved Reports' },
  { key: 'exports', label: 'Export History' },
];

const REPORT_TEMPLATES = [
  { name: 'Customer Satisfaction', description: 'CSAT and NPS metrics with drill-down', icon: FileBarChart, type: 'account_health' },
  { name: 'Team Productivity', description: 'Agent throughput and efficiency metrics', icon: Users, type: 'team_manager' },
  { name: 'Executive Summary', description: 'High-level KPIs and business metrics', icon: BarChart3, type: 'executive' },
  { name: 'Custom Report', description: 'Build a report from scratch', icon: Settings, type: 'custom' },
];

const STATUS_BADGES = {
  completed: { icon: CheckCircle, className: 'bg-green-100 text-green-700', label: 'Completed' },
  pending: { icon: Clock, className: 'bg-yellow-100 text-yellow-700', label: 'Pending' },
  failed: { icon: XCircle, className: 'bg-red-100 text-red-700', label: 'Failed' },
};

const FORMAT_BADGES = {
  csv: 'bg-blue-100 text-blue-700',
  json: 'bg-purple-100 text-purple-700',
};

// ── Sort helpers ──

function toggleSortState(current, field) {
  if (current.field === field) {
    return { field, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { field, dir: 'asc' };
}

function compareValues(a, b, field, dir) {
  const valA = a[field] ?? '';
  const valB = b[field] ?? '';
  const cmp = typeof valA === 'string'
    ? valA.localeCompare(valB, undefined, { sensitivity: 'base' })
    : valA - valB;
  return dir === 'asc' ? cmp : -cmp;
}

function SortIndicator({ field, sort }) {
  if (sort.field !== field) return null;
  return sort.dir === 'asc'
    ? <ChevronUp size={12} className="inline ml-0.5" />
    : <ChevronDown size={12} className="inline ml-0.5" />;
}

// ── Component ──

const ReportsPage = () => {
  // Templates exists only to create a report, so it is hidden without the write
  // permission. Saved Reports and Export History stay readable on analytics:read.
  const { hasPermission } = usePermissions();
  const canWriteReports = hasPermission(StandardPermission.Analytics.REPORTS);
  const visibleTabs = TABS.filter((tab) => tab.key !== 'templates' || canWriteReports);

  // Tab state from URL. The default is 'templates'; fall back to a visible tab
  // rather than stranding a read-only viewer on an empty page.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') || 'templates';
  const activeTab = visibleTabs.some((tab) => tab.key === requestedTab)
    ? requestedTab
    : 'reports';
  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });

  // Data state
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [exports, setExports] = useState([]);
  const [reportTotal, setReportTotal] = useState(0);
  const [exportTotal, setExportTotal] = useState(0);
  const mountedRef = useRef(false);

  // Template naming step
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [reportName, setReportName] = useState('');
  const [creating, setCreating] = useState(false);

  // Export controls
  const [exportFormats, setExportFormats] = useState({});
  const [exportingId, setExportingId] = useState(null);

  // Search
  const [reportSearch, setReportSearch] = useState('');
  const [exportSearch, setExportSearch] = useState('');

  // Sort
  const [reportSort, setReportSort] = useState({ field: 'created_at', dir: 'desc' });
  const [exportSort, setExportSort] = useState({ field: 'created_at', dir: 'desc' });

  // Expanded row
  const [expandedReportId, setExpandedReportId] = useState(null);

  // Two independent pagination instances
  const { page: rPage, pageSize: rPageSize, setPage: setRPage, setPageSize: setRPageSize } = usePagination(1, 10);
  const { page: ePage, pageSize: ePageSize, setPage: setEPage, setPageSize: setEPageSize } = usePagination(1, 10);

  // Shared date range for report generation
  const { from: exportFrom, to: exportTo, onChange: onExportDateChange } = useDateRange();

  // ── Data fetching ──

  const fetchReports = useCallback(async () => {
    try {
      const result = await getReports({ page: rPage, page_size: rPageSize });
      if (result.success) {
        setReports(result.data);
        setReportTotal(result.total ?? result.data?.length ?? 0);
      }
    } catch {
      toast.error('Failed to load reports');
    }
  }, [rPage, rPageSize]);

  const fetchExports = useCallback(async () => {
    try {
      const result = await getExports({ page: ePage, page_size: ePageSize });
      if (result.success) {
        setExports(result.data);
        setExportTotal(result.total ?? result.data?.length ?? 0);
      }
    } catch {
      toast.error('Failed to load exports');
    }
  }, [ePage, ePageSize]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchReports(), fetchExports()]);
      } finally {
        setLoading(false);
        mountedRef.current = true;
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mountedRef.current) fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    if (mountedRef.current) fetchExports();
  }, [fetchExports]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchReports(), fetchExports()]);
    } finally {
      setLoading(false);
    }
  };

  // ── Handlers ──

  const handleSelectTemplate = (template) => {
    setPendingTemplate(template);
    setReportName(template.name);
  };

  const handleCreate = async () => {
    if (!reportName.trim()) {
      toast.error('Report name is required');
      return;
    }
    setCreating(true);
    try {
      const result = await createReport({
        report_name: reportName.trim(),
        report_type: pendingTemplate.type,
      });
      if (result.success) {
        toast.success(`Report "${reportName.trim()}" created`);
        setPendingTemplate(null);
        setReportName('');
        setActiveTab('reports');
        fetchReports();
      } else {
        toast.error('Failed to create report. Please try again.');
      }
    } catch {
      toast.error('Failed to create report. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (report) => {
    const name = report.report_name || report.name || 'this report';
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      const result = await deleteReport(report.id);
      if (result.success) {
        toast.success(`Report "${name}" deleted`);
        setExpandedReportId(null);
        fetchReports();
      } else {
        toast.error(result.error || 'Failed to delete report');
      }
    } catch {
      toast.error('Failed to delete report');
    }
  };

  const handleExport = async (report) => {
    const format = exportFormats[report.id] || 'csv';
    setExportingId(report.id);
    try {
      const result = await exportReport(report.id, format, { from: exportFrom, to: exportTo });
      if (result.success) {
        toast.success('Export started');
        setActiveTab('exports');
        fetchExports();
      } else {
        toast.error(result.error || 'Failed to start export');
      }
    } catch {
      toast.error('Failed to start export');
    } finally {
      setExportingId(null);
    }
  };

  const handleDownload = async (exp) => {
    try {
      const result = await downloadExport(exp.id);
      if (result.success) {
        const url = window.URL.createObjectURL(new Blob([result.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = exp.report_name
          ? `${exp.report_name}.${exp.format || 'csv'}`
          : `export_${exp.id}.${exp.format || 'csv'}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error(result.error || 'Failed to download export');
      }
    } catch {
      toast.error('Failed to download export');
    }
  };

  // ── Filtered + sorted data ──

  const sortedReports = useMemo(() => {
    const filtered = reports.filter((r) =>
      (r.report_name || r.name || '').toLowerCase().includes(reportSearch.toLowerCase())
    );
    return [...filtered].sort((a, b) => compareValues(a, b, reportSort.field, reportSort.dir));
  }, [reports, reportSearch, reportSort]);

  const sortedExports = useMemo(() => {
    const filtered = exports.filter((e) =>
      (e.report_name || e.name || '').toLowerCase().includes(exportSearch.toLowerCase())
    );
    return [...filtered].sort((a, b) => compareValues(a, b, exportSort.field, exportSort.dir));
  }, [exports, exportSearch, exportSort]);

  // ── Render ──

  if (loading) return <Spinner />;

  return (
    <div>
      {/* Header: tabs + refresh */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex space-x-1 border-b border-gray-200">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <RefreshButton onClick={handleRefresh} loading={loading} />
      </div>

      {/* ── Templates Tab ── */}
      {activeTab === 'templates' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {REPORT_TEMPLATES.map((template) => {
              const Icon = template.icon;
              const isSelected = pendingTemplate?.type === template.type;
              return (
                <div
                  key={template.type}
                  onClick={() => handleSelectTemplate(template)}
                  className={`card-hover p-4 cursor-pointer transition-all ${
                    isSelected ? 'ring-2 ring-primary-500' : ''
                  }`}
                >
                  <div className="mb-2">
                    <Icon size={24} className="text-primary-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900">{template.name}</h4>
                  <p className="text-xs text-gray-500 mt-1">{template.description}</p>
                </div>
              );
            })}
          </div>

          {/* Naming step */}
          {pendingTemplate && (
            <div className="card p-4 mt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Create from: {pendingTemplate.name}
              </h4>
              <div className="flex items-end space-x-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Report Name</label>
                  <input
                    type="text"
                    className="input text-sm"
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                    autoFocus
                    placeholder="Enter report name..."
                  />
                </div>
                <button
                  onClick={handleCreate}
                  disabled={creating || !reportName.trim()}
                  className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
                <button
                  onClick={() => setPendingTemplate(null)}
                  className="text-gray-500 hover:text-gray-700 text-sm px-3 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Saved Reports Tab ── */}
      {activeTab === 'reports' && (
        <>
          {/* Search + Export Date Range */}
          <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
            <div className="relative max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search reports..."
                value={reportSearch}
                onChange={(e) => setReportSearch(e.target.value)}
                className="input text-sm pl-8 py-1.5"
              />
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">Export range:</span>
              <DateRangeFilter from={exportFrom} to={exportTo} onChange={onExportDateChange} showPresets />
            </div>
          </div>

          {sortedReports.length === 0 ? (
            <EmptyState
              title={reportSearch ? 'No matching reports' : 'No reports yet'}
              description={reportSearch ? 'Try a different search term.' : 'Create a report from the Templates tab to get started.'}
            />
          ) : (
            <div className="card">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th
                        onClick={() => setReportSort(toggleSortState(reportSort, 'report_name'))}
                        className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
                      >
                        Name <SortIndicator field="report_name" sort={reportSort} />
                      </th>
                      <th
                        onClick={() => setReportSort(toggleSortState(reportSort, 'report_type'))}
                        className="text-center px-5 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
                      >
                        Type <SortIndicator field="report_type" sort={reportSort} />
                      </th>
                      <th className="text-center px-5 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th
                        onClick={() => setReportSort(toggleSortState(reportSort, 'created_at'))}
                        className="text-center px-5 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
                      >
                        Created <SortIndicator field="created_at" sort={reportSort} />
                      </th>
                      <th className="text-center px-5 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedReports.map((report) => {
                      const name = report.report_name || report.name;
                      const format = exportFormats[report.id] || 'csv';
                      const isExpanded = expandedReportId === report.id;
                      return (
                        <>
                          <tr key={report.id || name} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="px-5 py-3">
                              <button
                                onClick={() => setExpandedReportId(isExpanded ? null : report.id)}
                                className="flex items-center space-x-2 text-left hover:underline"
                              >
                                <FileText size={16} className="text-gray-400 flex-shrink-0" />
                                <span className="font-medium text-gray-900">{name}</span>
                                {isExpanded
                                  ? <ChevronUp size={14} className="text-gray-400" />
                                  : <ChevronDown size={14} className="text-gray-400" />}
                              </button>
                            </td>
                            <td className="px-5 py-3 text-center">
                              <span className={`badge ${report.report_type === 'executive' ? 'badge-primary' : 'badge-gray'}`}>
                                {report.report_type || report.type}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-center text-gray-500">
                              {report.is_active ? 'Active' : 'Inactive'}
                            </td>
                            <td className="px-5 py-3 text-center text-gray-500">
                              {report.created_at ? new Date(report.created_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-5 py-3">
                              <div className="flex items-center justify-center space-x-2">
                                {/* Export is a POST and Delete a DELETE against
                                    analytics_service, both analytics:reports. The
                                    format select only feeds the export, so it goes
                                    with them. */}
                                <PermissionGate permission={StandardPermission.Analytics.REPORTS}>
                                  <select
                                    className="text-xs border border-gray-300 rounded px-1.5 py-1"
                                    value={format}
                                    onChange={(e) => setExportFormats((prev) => ({ ...prev, [report.id]: e.target.value }))}
                                  >
                                    <option value="csv">CSV</option>
                                    <option value="json">JSON</option>
                                  </select>
                                  <button
                                    onClick={() => handleExport(report)}
                                    disabled={exportingId === report.id}
                                    className="text-primary-600 hover:text-primary-800 disabled:opacity-50"
                                    aria-label={`Export ${name} as ${format}`}
                                    title="Export report"
                                  >
                                    {exportingId === report.id ? (
                                      <span className="inline-block w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <Download size={16} />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleDelete(report)}
                                    className="text-gray-400 hover:text-red-600"
                                    aria-label={`Delete report ${name}`}
                                    title="Delete report"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </PermissionGate>
                              </div>
                            </td>
                          </tr>
                          {/* Expanded detail row */}
                          {isExpanded && (
                            <tr key={`${report.id}-detail`} className="bg-gray-50">
                              <td colSpan={5} className="px-5 py-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500 text-xs">Type</span>
                                    <p className="font-medium text-gray-900">{report.report_type}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 text-xs">Status</span>
                                    <p className="font-medium text-gray-900">{report.is_active ? 'Active' : 'Inactive'}</p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 text-xs">Created</span>
                                    <p className="font-medium text-gray-900">
                                      {report.created_at ? new Date(report.created_at).toLocaleString() : '-'}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 text-xs">ID</span>
                                    <p className="font-medium text-gray-900">{report.id}</p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 border-t border-gray-100">
                <Pagination
                  page={rPage}
                  pageSize={rPageSize}
                  total={reportTotal}
                  onPageChange={setRPage}
                  onPageSizeChange={setRPageSize}
                  loading={loading}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Export History Tab ── */}
      {activeTab === 'exports' && (
        <>
          {/* Search */}
          <div className="mb-4">
            <div className="relative max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search exports..."
                value={exportSearch}
                onChange={(e) => setExportSearch(e.target.value)}
                className="input text-sm pl-8 py-1.5"
              />
            </div>
          </div>

          {sortedExports.length === 0 ? (
            <EmptyState
              title={exportSearch ? 'No matching exports' : 'No exports yet'}
              description={exportSearch ? 'Try a different search term.' : 'Export a report from the Saved Reports tab to get started.'}
            />
          ) : (
            <div className="card">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th
                        onClick={() => setExportSort(toggleSortState(exportSort, 'report_name'))}
                        className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
                      >
                        Report <SortIndicator field="report_name" sort={exportSort} />
                      </th>
                      <th
                        onClick={() => setExportSort(toggleSortState(exportSort, 'format'))}
                        className="text-center px-5 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
                      >
                        Format <SortIndicator field="format" sort={exportSort} />
                      </th>
                      <th
                        onClick={() => setExportSort(toggleSortState(exportSort, 'status'))}
                        className="text-center px-5 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
                      >
                        Status <SortIndicator field="status" sort={exportSort} />
                      </th>
                      <th
                        onClick={() => setExportSort(toggleSortState(exportSort, 'created_at'))}
                        className="text-center px-5 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
                      >
                        Created <SortIndicator field="created_at" sort={exportSort} />
                      </th>
                      <th className="text-center px-5 py-3 text-xs font-medium text-gray-500 uppercase">Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedExports.map((exp) => {
                      const statusInfo = STATUS_BADGES[exp.status] || STATUS_BADGES.pending;
                      const StatusIcon = statusInfo.icon;
                      const expName = exp.report_name || exp.name || 'Report';
                      return (
                        <tr key={exp.id || exp.execution_id} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <div className="flex items-center space-x-2">
                              <FileText size={16} className="text-gray-400" />
                              <span className="font-medium text-gray-900">{expName}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded-full uppercase font-medium ${FORMAT_BADGES[exp.format] || FORMAT_BADGES.csv}`}>
                              {exp.format || 'csv'}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-center">
                            <span className={`inline-flex items-center space-x-1 text-xs px-2 py-0.5 rounded-full ${statusInfo.className}`}>
                              <StatusIcon size={12} />
                              <span>{statusInfo.label}</span>
                            </span>
                          </td>
                          <td className="px-5 py-3 text-center text-gray-500">
                            {exp.created_at ? new Date(exp.created_at).toLocaleString() : '-'}
                          </td>
                          <td className="px-5 py-3 text-center">
                            {exp.status === 'completed' && exp.file_path ? (
                              <button
                                onClick={() => handleDownload(exp)}
                                className="text-primary-600 hover:text-primary-800 inline-block"
                                aria-label={`Download ${expName} ${exp.format || 'csv'} export`}
                                title="Download export"
                              >
                                <Download size={16} />
                              </button>
                            ) : (
                              <span className="text-gray-300" aria-label="Export not available">
                                <Download size={16} />
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 border-t border-gray-100">
                <Pagination
                  page={ePage}
                  pageSize={ePageSize}
                  total={exportTotal}
                  onPageChange={setEPage}
                  onPageSizeChange={setEPageSize}
                  loading={loading}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReportsPage;
